import { useEffect, useState } from "react";
import { Film, PlayCircle, Tv, LayoutPanelLeft, Archive, BarChart3, BookOpen, Bot, Hourglass, ClipboardList, Download, Globe, GraduationCap, HeartHandshake, Layers, ListChecks, Megaphone, MessageCircle, MessageSquare, Quote, Send, Share2, ShieldCheck, Sparkles, Type, UserCheck, Users, Video } from "lucide-react";
import { ADMIN_SECTIONS, sectionLabelNo } from "./sections";
import { jumpTo, useActiveSection } from "./AdminSidebar";

export const NAV_GROUPS = [
  { id: "website", title: "Website", icon: Globe, color: "#E8F1FB", ink: "#2B5D8C", items: ["site", "announcement", "social", "festival", "programs", "demo", "layout"] },
  { id: "participants", title: "Participants", icon: Users, color: "#EAF6EE", ink: "#2F6B4F", items: ["batch", "registrations", "approval", "export", "past", "security"] },
  { id: "messaging", title: "Messaging", icon: MessageCircle, color: "#FDF1E7", ink: "#B35A3B", items: ["welcome", "chatbot", "wa-hub"] },
  { id: "community", title: "Community", icon: HeartHandshake, color: "#F8EDF3", ink: "#8C3F67", items: ["feedback", "testimonials", "guides"] },
  { id: "classroom", title: "Classroom", icon: BookOpen, color: "#F3F0FA", ink: "#5B4A8A", items: ["classroom", "classroom-main", "classroom-videos", "classroom-reports", "classroom-player", "zoom", "present", "batch-report", "guide-info"] },
];

const ICONS = {
  security: ShieldCheck,
  batch: Layers, site: Type, announcement: Megaphone, social: Share2, festival: Sparkles, programs: ClipboardList, demo: PlayCircle,
  feedback: MessageSquare, testimonials: Quote, guides: Users, past: Archive,
  welcome: Send, chatbot: Bot, "wa-hub": MessageCircle, export: Download, approval: Hourglass, classroom: GraduationCap, "classroom-main": PlayCircle, "classroom-videos": Film, "classroom-reports": BarChart3, "classroom-player": Tv, zoom: Video, present: Users, "batch-report": BarChart3, "guide-info": UserCheck, registrations: ListChecks, layout: LayoutPanelLeft,
};
const LABELS = { security: "Security & Log", guides: "Guides Page", "guide-info": "Guides Info", registrations: "Future Batch", programs: "Programs", demo: "Demo", batch: "Master Control", welcome: "Welcome Msg", chatbot: "TalkingBot & ChatBot", "wa-hub": "WhatsApp Section", past: "Past Batch", site: "Site Basics", social: "Social Links", festival: "Festival", announcement: "Announcement", feedback: "Feedback", testimonials: "Testimonials", classroom: "Classroom", "classroom-main": "Day 1–6 | Day 1-10 Videos & Schedule", "classroom-videos": "Classroom Videos", "classroom-reports": "Reports & Statistics", "classroom-player": "Video Player", zoom: "Zoom Attendance", present: "Present Batch", "batch-report": "Batch Report", export: "Export", approval: "Approvals", layout: "Page Layout" };
const label = (id) => LABELS[id] || ADMIN_SECTIONS.find((s) => s.id === id)?.title || id;
const no = (id) => sectionLabelNo(id);

export const RAIL_STYLES = {
  compact: { name: "Compact", width: 84, hint: "Icon with a short name" },
  list: { name: "List", width: 196, hint: "Icon beside the name" },
  tiles: { name: "Tiles", width: 112, hint: "Soft colour tiles" },
};

export const useRailStyle = () => {
  const [style, setStyle] = useState(() => (typeof window !== "undefined" && localStorage.getItem("pl-admin-rail")) || "compact");
  useEffect(() => { localStorage.setItem("pl-admin-rail", style); }, [style]);
  return [RAIL_STYLES[style] ? style : "compact", setStyle];
};

const Flyout = ({ g, active }) => (
  <div className="pointer-events-none group-hover:pointer-events-auto absolute left-full top-0 pl-2 opacity-0 group-hover:opacity-100 translate-x-1 group-hover:translate-x-0 transition-[opacity,transform] duration-200 z-50" data-testid={`category-menu-${g.id}`}>
    <div className="min-w-[220px] rounded-2xl border border-border bg-white text-foreground shadow-[0_18px_40px_rgba(26,46,38,0.16)] p-2">
      <p className="px-3 pt-1 pb-2 text-[10px] uppercase tracking-[0.25em]" style={{ color: g.ink }}>{g.title}</p>
      {g.items.map((id) => {
        const IIcon = ICONS[id] || Layers;
        const on = active === id;
        return (
          <button key={id} type="button" data-testid={`category-item-${id}`} onClick={() => jumpTo(id)} className={`w-full flex items-center gap-2.5 rounded-xl px-3 py-2 text-left text-sm transition-colors ${on ? "text-white" : "hover:bg-muted"}`} style={on ? { backgroundColor: g.ink } : undefined}>
            <IIcon size={15} /> <span className="flex-1">{label(id)}</span><span className={`text-[10px] tabular-nums ${on ? "text-white/70" : "text-muted-foreground"}`}>{no(id)}</span>
          </button>
        );
      })}
    </div>
  </div>
);

const StylePicker = ({ style, setStyle, compact }) => (
  <div className={`mt-auto pt-3 border-t border-[#1A2E26]/10 flex ${compact ? "flex-col items-center" : "items-center justify-between px-2"} gap-1`} data-testid="rail-style-picker">
    {!compact && <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Layout</span>}
    <div className="flex gap-1">
      {Object.entries(RAIL_STYLES).map(([k, v]) => (
        <button key={k} type="button" data-testid={`rail-style-${k}`} title={`${v.name} — ${v.hint}`} onClick={() => setStyle(k)} className={`w-6 h-6 rounded-md border text-[9px] font-semibold transition-colors ${style === k ? "bg-[#1A2E26] text-white border-[#1A2E26]" : "bg-white/70 border-border text-muted-foreground hover:border-[#1A2E26]/40"}`}>{v.name[0]}</button>
      ))}
    </div>
  </div>
);

export const CategoryRail = ({ style, setStyle }) => {
  const active = useActiveSection();
  const activeGroup = NAV_GROUPS.find((g) => g.items.includes(active));
  const width = RAIL_STYLES[style].width;

  return (
    <aside data-testid="admin-category-rail" data-style={style} className="hidden xl:flex fixed left-0 top-0 bottom-0 flex-col z-50 bg-[#F4F6F1] border-r border-[#1A2E26]/10 px-2 py-4" style={{ width }}>
      <div className={`flex items-center gap-2 mb-4 ${style === "list" ? "px-2" : "justify-center"}`}>
        <span className="grid place-items-center w-9 h-9 rounded-xl bg-[#1A2E26] text-white font-serif text-base">PL</span>
        {style === "list" && <span className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground">Admin</span>}
      </div>

      <nav className="flex flex-col gap-1.5" aria-label="Admin categories">
        {NAV_GROUPS.map((g) => {
          const Icon = g.icon;
          const isActive = activeGroup?.id === g.id;
          const base = "relative group w-full";
          if (style === "list") {
            return (
              <div key={g.id} className={base}>
                <button type="button" data-testid={`category-${g.id}`} onClick={() => jumpTo(g.items[0])} className={`w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors ${isActive ? "shadow-sm" : "hover:bg-white/70"}`} style={isActive ? { backgroundColor: g.color } : undefined}>
                  <span className="grid place-items-center w-8 h-8 rounded-lg" style={{ backgroundColor: isActive ? "#fff" : g.color, color: g.ink }}><Icon size={17} /></span>
                  <span className="flex-1 min-w-0"><span className="block text-[13px] font-medium text-foreground truncate">{g.title}</span><span className="block text-[10px] text-muted-foreground">{g.items.length} sections</span></span>
                </button>
                <Flyout g={g} active={active} />
              </div>
            );
          }
          if (style === "tiles") {
            return (
              <div key={g.id} className={base}>
                <button type="button" data-testid={`category-${g.id}`} onClick={() => jumpTo(g.items[0])} className={`w-full flex flex-col items-center gap-1.5 rounded-2xl py-3 transition-[transform,box-shadow] hover:-translate-y-0.5 ${isActive ? "shadow-[0_8px_20px_rgba(26,46,38,0.12)] ring-2" : ""}`} style={{ backgroundColor: g.color, color: g.ink, "--tw-ring-color": g.ink }}>
                  <Icon size={22} />
                  <span className="text-[11px] font-medium leading-tight">{g.title}</span>
                </button>
                <Flyout g={g} active={active} />
              </div>
            );
          }
          return (
            <div key={g.id} className={`${base} flex justify-center`}>
              <button type="button" data-testid={`category-${g.id}`} onClick={() => jumpTo(g.items[0])} className={`w-[68px] flex flex-col items-center gap-1 rounded-xl py-2 transition-colors ${isActive ? "" : "hover:bg-white/80"}`} style={isActive ? { backgroundColor: g.color, color: g.ink } : { color: "#1A2E26" }}>
                <Icon size={20} />
                <span className="text-[10px] leading-tight tracking-wide">{g.title}</span>
              </button>
              <Flyout g={g} active={active} />
            </div>
          );
        })}
      </nav>

      <StylePicker style={style} setStyle={setStyle} compact={style !== "list"} />
    </aside>
  );
};
